// Rep virtuals — client types + calls (API: apps/api/src/routes/virtuals.ts).
import { api, apiBlob, ApiError } from "@/lib/api";

export interface Box { x: number; y: number; w: number; h: number }
export interface ImprintArea { box: Box; item: Box; angleDeg: number; surface: "flat" | "cylinder"; engine?: string; note?: string | null }
export interface ImprintSpec {
  method: string;
  location: string | null;
  maxWidthIn: number | null;
  maxHeightIn: number | null;
  maxDiameterIn: number | null;
  itemEdgesIn: number[] | null;
  estimated: boolean;
}
export interface Placement { scale: number; dx: number; dy: number }
export type DecorationStyle = "one_colour" | "full_colour" | "laser" | "deboss";

export interface Virtual {
  id: string;
  setId: string;
  presentationItemId: string | null;
  productId: string | null;
  productName: string;
  colour: string | null;
  sourceKind: "catalogue" | "url" | "upload";
  sourceUrl: string | null;
  spec: ImprintSpec;
  area: ImprintArea;
  placement: Placement;
  style: DecorationStyle;
  inkHex: string | null;
  imageUrl: string | null;
  realismUrl: string | null;
  photoUrl: string;
  realism: { status?: string; engine?: string | null; message?: string | null };
  composite: { logoSizeIn?: { w: number; h: number } | null; width?: number; height?: number; imprint?: { cx: number; cy: number; w: number; h: number; angleDeg: number; estimated: boolean } };
  flags: string[];
  status: "ready" | "approved" | "rejected" | "sent_to_designer" | "failed";
  approvedImage: "composite" | "realism" | null;
  costCents: number;
  version: number;
  estimated: boolean;
}

export interface VirtualSet {
  id: string;
  clientId: string | null;
  presentationId: string | null;
  projectId: string | null;
  title: string | null;
  logoFileId: string;
  logoUrl: string;
  prep: {
    width: number;
    height: number;
    palette: string[];
    rule: { colours: number | null; print: "spot" | "full_colour" | "unknown"; summary: string };
    lowResolution: boolean;
    backgroundRemoved: boolean;
    notes: string[];
  };
  shareToken: string | null;
  virtuals: Virtual[];
}

export interface CatalogueColour { colour: string; photoUrl: string; blank: boolean }
export interface CatalogueProduct { productId: string; name: string; supplier: string | null; spec: ImprintSpec; colours: CatalogueColour[] }

export interface PageRead {
  ok: true;
  url: string;
  host: string;
  name: string | null;
  images: Array<{ url: string; colour: string | null; alt: string | null }>;
  spec: Omit<ImprintSpec, "estimated"> & { colours: string[]; source: "page" | "ai" | null };
}
export interface PageBlocked { ok: false; url: string; reason: string; message: string }

export type GenerateTarget =
  | { kind: "item"; presentationItemId: string; colours: string[] }
  | { kind: "catalogue"; productId: string; colours: string[] }
  | { kind: "url"; sourceUrl: string; imageUrl: string; name: string; colour: string | null; spec: Partial<ImprintSpec> }
  | { kind: "upload"; photoKey: string; name: string; colour: string | null; spec: Partial<ImprintSpec> };

export interface Spend { spentCents: number; capCents: number }

export const ESTIMATED_LABEL = "Imprint size estimated — confirm with supplier";

export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const b = e.body as { error?: string } | null;
    if (b?.error) return b.error;
    if (e.status === 403) return "You don't have access to virtuals.";
  }
  return e instanceof Error ? e.message : "Something went wrong.";
}

export const virtualsApi = {
  spend: () => api<Spend>("/virtuals/spend"),
  getSet: (id: string) => api<{ set: VirtualSet }>(`/virtuals/sets/${id}`).then((r) => r.set),
  uploadLogo: (file: File, opts: { clientId?: string | null; presentationId?: string | null; title?: string | null }) => {
    const q = new URLSearchParams({ filename: file.name });
    if (opts.clientId) q.set("client_id", opts.clientId);
    if (opts.presentationId) q.set("presentation_id", opts.presentationId);
    if (opts.title) q.set("title", opts.title);
    return api<{ set: VirtualSet }>(`/virtuals/sets/upload?${q}`, { method: "POST", body: file, headers: { "Content-Type": file.type || "application/octet-stream" } }).then((r) => r.set);
  },
  setFromFile: (fileId: string, opts: { clientId?: string | null; presentationId?: string | null; title?: string | null }) =>
    api<{ set: VirtualSet }>("/virtuals/sets", { method: "POST", body: JSON.stringify({ logo_file_id: fileId, client_id: opts.clientId ?? null, presentation_id: opts.presentationId ?? null, title: opts.title ?? null }) }).then((r) => r.set),
  generate: (setId: string, targets: GenerateTarget[], realism: boolean) =>
    api<{ virtuals: Virtual[]; errors: Array<{ productName: string; colour: string | null; message: string }>; spend: Spend }>(`/virtuals/sets/${setId}/generate`, { method: "POST", body: JSON.stringify({ targets, realism }) }),
  nudge: (id: string, body: { area?: ImprintArea; placement?: Placement; ink_hex?: string | null; style?: DecorationStyle }) =>
    api<{ virtual: Virtual }>(`/virtuals/${id}`, { method: "PATCH", body: JSON.stringify(body) }).then((r) => r.virtual),
  regenerate: (id: string, realism: boolean) => api<{ virtual: Virtual; spend: Spend }>(`/virtuals/${id}/regenerate`, { method: "POST", body: JSON.stringify({ realism }) }),
  approve: (id: string, image: "composite" | "realism") => api<{ virtual: Virtual }>(`/virtuals/${id}/approve`, { method: "POST", body: JSON.stringify({ image }) }).then((r) => r.virtual),
  reject: (id: string) => api<{ virtual: Virtual }>(`/virtuals/${id}/reject`, { method: "POST", body: "{}" }).then((r) => r.virtual),
  reopen: (id: string) => api<{ virtual: Virtual }>(`/virtuals/${id}/reopen`, { method: "POST", body: "{}" }).then((r) => r.virtual),
  sendToDesigner: (id: string, note: string | null) => api<{ virtual: Virtual }>(`/virtuals/${id}/send-to-designer`, { method: "POST", body: JSON.stringify({ note }) }).then((r) => r.virtual),
  share: (setId: string) => api<{ token: string; url: string }>(`/virtuals/sets/${setId}/share`, { method: "POST", body: "{}" }),
  attach: (setId: string, projectId: string) => api<{ attached: number }>(`/virtuals/sets/${setId}/attach`, { method: "POST", body: JSON.stringify({ project_id: projectId }) }),
  pdf: (setId: string) => apiBlob(`/virtuals/sets/${setId}/pdf`),
  png: (id: string) => apiBlob(`/virtuals/${id}/download`),
  readUrl: (url: string) => api<{ page: PageRead | PageBlocked }>("/virtuals/source/url", { method: "POST", body: JSON.stringify({ url }) }).then((r) => r.page),
  uploadPhoto: (file: File) => api<{ photoKey: string; width: number; height: number }>("/virtuals/source/photo", { method: "POST", body: file, headers: { "Content-Type": file.type || "application/octet-stream" } }),
  listing: (merchdId: string) => api<{ product: CatalogueProduct }>(`/virtuals/catalogue/listing/${merchdId}`).then((r) => r.product),
  clientLogos: (clientId: string) => api<{ rows: Array<{ fileId: string; filename: string; url: string }> }>(`/virtuals/client-logos?client_id=${clientId}`).then((r) => r.rows),
  catalogueRequest: (body: { virtual_id?: string | null; url?: string | null; name?: string | null; note?: string | null }) => api<{ id: string }>("/virtuals/catalogue-requests", { method: "POST", body: JSON.stringify(body) }),
  presentationOptions: (presentationId: string) =>
    api<{ items: Array<{ itemId: string; productId: string; name: string; colours: CatalogueColour[]; spec: ImprintSpec | null; problem: string | null }> }>(`/presentations/${presentationId}/virtual-options`).then((r) => r.items),
  presentationVirtuals: (presentationId: string) =>
    api<{ approved: Array<{ itemId: string; virtualId: string; colour: string | null; imageUrl: string }>; latestSetId: string | null }>(`/presentations/${presentationId}/virtuals`),
};

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export function specLine(s: ImprintSpec | null): string {
  if (!s) return "No supplier imprint details";
  const size = s.maxDiameterIn ? `${trim(s.maxDiameterIn)}" diameter` : s.maxWidthIn ? `${trim(s.maxWidthIn)}" × ${s.maxHeightIn ? `${trim(s.maxHeightIn)}"` : "?"} max` : "size not stated";
  return [s.method, s.location, size].filter(Boolean).join(" · ");
}
const trim = (n: number) => String(Math.round(n * 100) / 100);

export const STYLE_LABEL: Record<DecorationStyle, string> = {
  one_colour: "One-colour print",
  full_colour: "Full color",
  laser: "Laser engraved",
  deboss: "Debossed",
};
